import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {AspectRatio, useBreakpointValue} from '@chakra-ui/react'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {isServer} from '../utils/utils'
import {breakpoints} from '../theme/_utils'
import React from 'react'

export const getDamBaseUrl = () => {
    const {customParams} = getConfig()
    const baseUrl = `${customParams?.dam?.protocol}://${customParams?.dam?.domain}`

    return baseUrl
}

export const getDamProductUrl = () => {
    const {customParams} = getConfig()
    const baseUrl = `${customParams?.dam?.protocol}://${customParams?.dam?.products}`

    return baseUrl
}

export const windowGetBreakpoint = (width) => {
    if (width < parseInt(breakpoints.md)) return 'mobile'
    else if (width < parseInt(breakpoints.lg)) return 'tablet_768'
    else if (width < parseInt(breakpoints.xl)) return 'tablet_1024'
    else return 'desktop'
}

/**
 * Ratio utils
 */

const renditions = [{key: 'mobile'}, {key: 'tablet_768'}, {key: 'tablet_1024'}, {key: 'desktop'}]

const getAspectRatioValue = (rendition) => {
    const [w, h] = rendition.split('x').map(Number)
    if (isNaN(w) || isNaN(h) || h === 0) return null
    return (w / h).toFixed(3) // ad esempio 1.778 per 16:9
}

const keys = renditions.map((r) => r.key)
const getSingleAspectRatio = (preferredKeys, aspectRatioMap) => {
    for (const key of preferredKeys) {
        if (keys.includes(key)) return Number(aspectRatioMap[key])
    }
    return null
}

const getRatioObject = (aspectRatioMap) => {
    const ratios = {}
    const base = getSingleAspectRatio(
        ['mobile', 'tablet_768', 'tablet_1024', 'desktop'],
        aspectRatioMap,
    )
    const md = getSingleAspectRatio(['tablet_768', 'tablet_1024', 'desktop'], aspectRatioMap)
    const lg = getSingleAspectRatio(['tablet_1024', 'desktop'], aspectRatioMap)
    const xl = getSingleAspectRatio(['desktop'], aspectRatioMap)

    if (base) ratios.base = base
    if (md) ratios.md = md
    if (lg) ratios.lg = lg
    if (xl) ratios.xl = xl

    return ratios
}

export const getDamImagePicture = (contentStackObj, altImg = '', isFirstElement = false) => {
    if (!contentStackObj) return null

    const baseUrl = getDamBaseUrl()

    const breakpoints = [
        {key: 'mobile', media: '(max-width: 767px)'},
        {key: 'tablet_768', media: '(min-width: 768px) and (max-width: 1023px)'},
        {key: 'tablet_1024', media: '(min-width: 1024px) and (max-width: 1279px)'},
        {key: 'desktop', media: '(min-width: 1280px)'},
    ]

    const aspectRatioMap = {}

    const sources = breakpoints
        .map(({key, media}) => {
            const rendition = contentStackObj?.[key]?.rendition
            const damImage = contentStackObj?.[key]?.dam_image?.[0]

            if (!rendition || !damImage?.id) return null

            aspectRatioMap[key] = getAspectRatioValue(rendition)

            const url = `${baseUrl}/${rendition}/assets/${damImage.id}`

            return <source key={key} srcSet={url} media={media} />
        })
        .filter(Boolean)

    const ratio = getRatioObject(aspectRatioMap)

    const fallback = breakpoints.find(({key}) => {
        const rendition = contentStackObj?.[key]?.rendition
        const damImage = contentStackObj?.[key]?.dam_image?.[0]
        return rendition && damImage?.id
    })

    if (!fallback) return null

    const fallbackUrl = `${baseUrl}/${contentStackObj[fallback.key]?.rendition}/assets/${
        contentStackObj[fallback.key]?.dam_image[0]?.id
    }`

    if (!fallbackUrl) return null

    return (
        <AspectRatio data-id="aspect-ratio-dam-image" ratio={ratio} width="100%">
            <picture
                style={{
                    width: '100%',
                }}
            >
                {sources}
                <img
                    src={fallbackUrl}
                    alt={altImg}
                    width={'100%'}
                    // eslint-disable-next-line react/no-unknown-property
                    fetchpriority={isFirstElement ? 'high' : 'low'}
                />
            </picture>
        </AspectRatio>
    )
}

/**
 *
 * @param {object} contentStackObj - content stack field that contains the image obj
 * @returns {string} - full path to the image
 *
 */
const useDamImage = (contentStackObj) => {
    const {res} = useServerContext()

    let breakpoint = useBreakpointValue(
        {
            base: 'mobile',
            md: 'tablet_768',
            lg: 'tablet_1024',
            xl: 'desktop',
        },
        {
            fallback: null,
        },
    )

    if (!breakpoint) {
        if (res?.locals?.ua?.isMobile) breakpoint = 'mobile'
        else if (res?.locals?.ua?.isTablet) breakpoint = 'tablet_1024'
        else if (res?.locals?.ua?.isDesktop) breakpoint = 'desktop'
        else if (isServer) breakpoint = 'mobile'
        else {
            breakpoint = windowGetBreakpoint(window?.innerWidth)
        }
    }

    const DEFAULT_RENDITION = 16 / 9
    const EMPTY_DAM_IMAGE = {
        url: '',
        ratio: DEFAULT_RENDITION,
    }

    if (!contentStackObj || !/x/.test(contentStackObj?.[breakpoint]?.rendition))
        return EMPTY_DAM_IMAGE

    let damImage = contentStackObj?.[breakpoint]?.dam_image?.[0]

    if (!damImage && breakpoint === 'tablet_768') {
        damImage = contentStackObj?.['mobile']?.dam_image?.[0]
    }

    if (!damImage && breakpoint === 'tablet_1024') {
        damImage = contentStackObj?.['desktop']?.dam_image?.[0]
    }

    const baseUrl = getDamBaseUrl()
    const ratioFactors = contentStackObj[breakpoint].rendition.split('x')

    return {
        url: damImage?.id
            ? `${baseUrl}/${contentStackObj[breakpoint].rendition}/assets/${damImage?.id}`
            : '',
        ratio: ratioFactors[1] ? ratioFactors[0] / ratioFactors[1] : DEFAULT_RENDITION,
    }
}

export default useDamImage
